"use client";

import { useEffect, useState } from "react";
import { m as motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import {
  ArrowRight,
  Bot,
  Boxes,
  Cpu,
  Database,
  Download,
  Expand,
  FileText,
  Layers,
  ServerCog,
  Sparkles,
} from "lucide-react";
import { EngineeringProfileHeroVisual } from "./EngineeringProfileHeroVisual";
import { Layout } from "@/components/layout/Layout";
import { InBreve } from "@/components/shared/InBreve";
import { Button } from "@/components/ui/button";
import { localizedRoutes } from "@/lib/routes/routes";
import { trackEvent } from "@/components/GoogleAnalytics";
import { exportPresentationDeckPptx } from "@/features/presentation/export/exportPresentationDeckPptx";
import type { PresentationSourceData } from "@/features/presentation/slides/types";
import {
  getDownloadMetrics,
  recordDownloadMetric,
  type DownloadMetricsState,
} from "@/features/presentation/monitoring/downloadMetrics";
import { AuthProvider, useAuth } from "@/hooks/useAuth";

type TechGroup = {
  category: string;
  icon: React.ComponentType<{ className?: string }>;
  items: string[];
};

type Project = {
  title: string;
  role: string;
  stack: string[];
  problem: string;
  action: string;
  impact: string;
  client?: string;
};

type ExperienceLogo = {
  name: string;
  src: string;
  sizeClass?: string;
};

/** Download a static file safely via fetch + blob (no browser security warnings). */
async function downloadStaticFile(url: string, fileName: string) {
  const res = await fetch(url);
  const blob = new Blob([await res.arrayBuffer()], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Delay revoke to give browser time to start the download
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  }, 1000);
}

type DownloadEngineeringSlidesPdfParams = {
  fileName: string;
  isItalian: boolean;
  heroTitle: string;
  heroSubtitle: string;
  trustLine: string;
  heroStack: string[];
  techGroups: { category: string; items: string[] }[];
  mindsetItems: string[];
  freelanceItems: string[];
  projects: Project[];
  problemLabel: string;
  actionLabel: string;
  impactLabel: string;
};

async function downloadEngineeringSlidesPdf({
  fileName,
  isItalian,
  heroTitle: _heroTitle,
  heroSubtitle: _heroSubtitle,
  trustLine: _trustLine,
  heroStack: _heroStack,
  techGroups: _techGroups,
  mindsetItems: _mindsetItems,
  freelanceItems: _freelanceItems,
  projects: _projects,
  problemLabel: _problemLabel,
  actionLabel: _actionLabel,
  impactLabel: _impactLabel,
}: DownloadEngineeringSlidesPdfParams) {
  await exportPresentationDeckPptx({
    preset: "presentation",
    locale: isItalian ? "it" : "en",
    fileName,
  });
}

function EngineeringProfileInner() {
  const locale = useLocale();
  const router = useRouter();
  const { isAdmin } = useAuth();
  const isItalian = locale.startsWith("it");
  const routes = localizedRoutes[isItalian ? "it" : "en"];
  const [metrics, setMetrics] = useState<DownloadMetricsState | null>(null);

  const copy = isItalian
    ? {
        heroEyebrow: "La mia esperienza in Accenture",
        heroTitle: "Ex Accenture, ora <accent>Freelance Full Stack Developer</accent>",
        heroSubtitle:
          "Dall'esperienza enterprise in Accenture a progetti freelance full-stack orientati a prodotto, performance e delivery concreto. Oggi porto quella solidità in build più snelle, veloci e leggibili.",
        trustLine: "Freelance Full Stack Developer",
        downloadMain: "Scarica CV tecnico (PDF)",
        contact: "Contattami",
        techTitle: "Tecnologie con cui lavoro",
        techSubtitle:
          "Stack reali usati su progetti enterprise, piattaforme custom e sistemi ad alta complessità.",
        projectsTitle: "Progetti su cui ho lavorato",
        projectsSubtitle:
          "Timeline dei progetti con ruolo, stack, problema, intervento e impatto tecnico.",
        problem: "Problema",
        action: "Intervento",
        impact: "Impatto",
        mindsetTitle: "Come affronto lo sviluppo software",
        mindsetSubtitle:
          "Approccio pragmatico e orientato al prodotto: meno complessità teorica, più chiarezza tecnica e più impatto reale.",
        freelanceTitle: "Visione freelance - Dimensione 4",
        freelanceSubtitle:
          "Dopo un percorso enterprise, oggi porto in Dimensione 4 un taglio più snello, diretto e premium su prodotti software ad alto valore tecnico.",
        discuss: "Parliamone",
        logosTitle: "Aziende e brand con cui ho lavorato",
        logosSubtitle:
          "Contesti professionali in cui ho lavorato come sviluppatore all'interno di team strutturati.",
        logosNote: "I marchi appartengono ai rispettivi proprietari.",
        pdfTitle: "Download profilo professionale",
        pdfSubtitle:
          "Scarica il CV tecnico completo, il documento Vision & Mission oppure il CV aggiornato con esperienze aziendali.",
        downloadFull: "Download CV tecnico completo",
        downloadExperience: "Download Vision & Mission",
        downloadCvAziendale: "Scarica CV aggiornato (PDF)",
        cvAziendaleFileName: "CV_Dario_Marco_Bellini_compressed.pdf",
        downloadSlides: "Download deck presentation (.pptx)",
        openPresentation: "Apri presentazione",
        privateMonitoring: "Monitoring privato download",
        pdfFullFileName: "dimensione4-cv-tecnico-freelance.pdf",
        pdfExperienceFileName: "dimensione4-vision-mission.pdf",
        pdfSlidesFileName: "dimensione4-presentazione-engineering-deck.pptx",
      }
    : {
        heroEyebrow: "My Accenture experience",
        heroTitle: "Ex-Accenture, now <accent>Freelance Full Stack Developer</accent>",
        heroSubtitle:
          "From Accenture enterprise delivery to freelance full-stack work focused on product, performance, and execution. Today I bring that foundation into leaner, faster, and sharper builds.",
        trustLine: "Freelance Full Stack Developer",
        downloadMain: "Download technical CV (PDF)",
        contact: "Contact me",
        techTitle: "Technologies I Work With",
        techSubtitle:
          "Real-world stack used across enterprise projects, custom platforms, and high-complexity systems.",
        projectsTitle: "Projects I worked on",
        projectsSubtitle:
          "Timeline of projects with role, stack, problem, implementation, and technical impact.",
        problem: "Problem",
        action: "Action",
        impact: "Impact",
        mindsetTitle: "How I Approach Software Development",
        mindsetSubtitle:
          "A pragmatic, product-oriented approach: less theoretical complexity, more technical clarity and real impact.",
        freelanceTitle: "Freelance Vision - Dimensione 4",
        freelanceSubtitle:
          "After an enterprise path, I now bring a leaner, more direct Dimension 4 approach to software products with strong technical value.",
        discuss: "Let's discuss it",
        logosTitle: "Companies and brands I worked with",
        logosSubtitle:
          "Professional contexts where I worked as a software engineer inside structured teams.",
        logosNote: "Trademarks belong to their respective owners.",
        pdfTitle: "Download Professional Profile",
        pdfSubtitle:
          "Download the full technical CV, the Vision & Mission document, or the updated CV with company experience.",
        downloadFull: "Download full technical CV",
        downloadExperience: "Download Vision & Mission",
        downloadCvAziendale: "Download updated CV (PDF)",
        cvAziendaleFileName: "CV_Dario_Marco_Bellini_compressed.pdf",
        downloadSlides: "Download presentation deck (.pptx)",
        openPresentation: "Open presentation",
        privateMonitoring: "Private download monitoring",
        pdfFullFileName: "dimensione4-freelance-technical-cv.pdf",
        pdfExperienceFileName: "dimensione4-vision-mission-en.pdf",
        pdfSlidesFileName: "dimensione4-engineering-presentation-deck.pptx",
      };
  const showPresentationDownload = false;

  const heroStack = [
    "Next.js",
    "React",
    "Angular",
    "TypeScript",
    "Python",
    "PostgreSQL",
    "Docker",
    "Flutter",
  ];

  const techGroups: TechGroup[] = isItalian
    ? [
        {
          category: "Frontend",
          icon: Layers,
          items: ["Next.js", "React", "Angular", "TypeScript"],
        },
        {
          category: "Backend",
          icon: ServerCog,
          items: ["Python", "FastAPI", "Node.js"],
        },
        {
          category: "Infrastruttura",
          icon: Cpu,
          items: ["Docker", "Kubernetes", "VPS"],
        },
        {
          category: "Dati",
          icon: Database,
          items: ["PostgreSQL", "TimescaleDB", "Redis", "Couchbase"],
        },
        {
          category: "AI",
          icon: Bot,
          items: ["LangChain", "LangFuse", "RAG systems"],
        },
        {
          category: "3D e Visualizzazione",
          icon: Boxes,
          items: ["Three.js", "React Three Fiber", "Blender"],
        },
      ]
    : [
        {
          category: "Frontend",
          icon: Layers,
          items: ["Next.js", "React", "Angular", "TypeScript"],
        },
        {
          category: "Backend",
          icon: ServerCog,
          items: ["Python", "FastAPI", "Node.js"],
        },
        {
          category: "Infrastructure",
          icon: Cpu,
          items: ["Docker", "Kubernetes", "VPS"],
        },
        {
          category: "Data",
          icon: Database,
          items: ["PostgreSQL", "TimescaleDB", "Redis", "Couchbase"],
        },
        {
          category: "AI",
          icon: Bot,
          items: ["LangChain", "LangFuse", "RAG systems"],
        },
        {
          category: "3D & Visualization",
          icon: Boxes,
          items: ["Three.js", "React Three Fiber", "Blender"],
        },
      ];

  const projects: Project[] = isItalian
    ? [
        {
          title: "Digital Twin Platform",
          role: "Front-End Developer",
          stack: ["React", "Three.js", "Potree", "WebODM", "AWS SageMaker"],
          problem:
            "Visualizzazione point-cloud pesante con frame-rate instabile e navigazione 3D poco fluida.",
          action:
            "Ho riprogettato pipeline e rendering con Potree + Three.js, ottimizzando LOD, gestione asset e interazioni camera.",
          impact:
            "Navigazione 3D più stabile, latenza ridotta e migliore qualità visiva su dataset industriali complessi.",
        },
        {
          title: "Connected Industrial Worker",
          role: "Full Stack Developer",
          client: "Saipem",
          stack: [
            "C#",
            "Xamarin",
            "Java Spring Boot",
            "Couchbase",
            "Kubernetes",
            "MinIO",
          ],
          problem:
            "Bug ricorrenti su tablet industriali, reportistica fragile e criticità nella sincronizzazione dati.",
          action:
            "Ho gestito debugging cross-platform, query N1QL ottimizzate e stabilizzazione dei flussi PDF/Excel con monitoraggio più solido.",
          impact:
            "Miglioramento stabilità applicativa, riduzione blocchi operativi e maggiore affidabilità in campo.",
        },
        {
          title: "AI Assistant Platform",
          role: "Backend & AI Developer",
          client: "VVF - Vigili del Fuoco",
          stack: [
            "Python",
            "FastAPI",
            "Docker",
            "PostgreSQL",
            "Redis",
            "LangChain",
            "LangFuse",
          ],
          problem:
            "Architettura basata su SSE difficile da integrare in ecosistemi REST enterprise e gateway pubblici.",
          action:
            "Ho convertito il flusso in API RESTful con polling e stato su Redis, mantenendo progressività della risposta utente.",
          impact:
            "Integrazione più semplice in ambienti enterprise, UX conservata e maggiore robustezza operativa.",
        },
        {
          title: "IoT Platform",
          role: "Full Stack Developer",
          stack: [
            "Android BLE",
            "ESP32-S3",
            "Python FastAPI",
            "PostgreSQL",
            "TimescaleDB",
            "React",
            "Firebase",
          ],
          problem:
            "Assenza di un flusso end-to-end coerente tra dispositivo embedded, backend e dashboard dati.",
          action:
            "Ho costruito integrazione completa device-cloud-frontend con telemetria strutturata e basi dati time-series.",
          impact:
            "PoC finanziata e trasformata in startup con una base tecnica scalabile.",
        },
        {
          title: "Digital Payments Platform",
          role: "Full Stack Developer",
          client: "Nexi / pagoPA",
          stack: ["Angular", "Angular Material", "Java Spring Boot"],
          problem:
            "Frontend poco modulare e complessità elevata nella manutenzione di componenti e documentazione PDF.",
          action:
            "Ho refattorizzato architettura Angular, standardizzato componenti e stabilizzato la generazione documentale.",
          impact:
            "Codebase più manutenibile, onboarding tecnico più rapido e riduzione del debito frontend.",
        },
      ]
    : [
        {
          title: "Digital Twin Platform",
          role: "Front-End Developer",
          stack: ["React", "Three.js", "Potree", "WebODM", "AWS SageMaker"],
          problem:
            "Heavy point-cloud rendering with unstable frame rates and poor 3D navigation quality.",
          action:
            "I redesigned rendering and asset flow with Potree + Three.js, optimizing LOD strategy and camera interactions.",
          impact:
            "More stable 3D navigation, lower latency, and better visual quality on complex industrial datasets.",
        },
        {
          title: "Connected Industrial Worker",
          role: "Full Stack Developer",
          client: "Saipem",
          stack: [
            "C#",
            "Xamarin",
            "Java Spring Boot",
            "Couchbase",
            "Kubernetes",
            "MinIO",
          ],
          problem:
            "Recurring issues on industrial tablets, fragile reporting workflows, and unstable sync behaviors.",
          action:
            "I handled cross-platform debugging, optimized N1QL queries, and hardened PDF/Excel reporting pipelines.",
          impact:
            "Higher application stability, fewer operational blockers, and improved field reliability.",
        },
        {
          title: "AI Assistant Platform",
          role: "Backend & AI Developer",
          client: "Italian Firefighters (VVF)",
          stack: [
            "Python",
            "FastAPI",
            "Docker",
            "PostgreSQL",
            "Redis",
            "LangChain",
            "LangFuse",
          ],
          problem:
            "SSE-based architecture was hard to integrate with enterprise REST ecosystems and public gateways.",
          action:
            "I converted the flow to RESTful APIs with polling and Redis-based state while preserving progressive UX.",
          impact:
            "Simpler enterprise integration, preserved user experience, and stronger operational robustness.",
        },
        {
          title: "IoT Platform",
          role: "Full Stack Developer",
          stack: [
            "Android BLE",
            "ESP32-S3",
            "Python FastAPI",
            "PostgreSQL",
            "TimescaleDB",
            "React",
            "Firebase",
          ],
          problem:
            "No coherent end-to-end flow between embedded device, backend, and data dashboard.",
          action:
            "I built a full device-cloud-frontend integration with structured telemetry and time-series data foundations.",
          impact: "Funded PoC, later evolved into a startup path.",
        },
        {
          title: "Digital Payments Platform",
          role: "Full Stack Developer",
          client: "Nexi / pagoPA",
          stack: ["Angular", "Angular Material", "Java Spring Boot"],
          problem:
            "Low frontend modularity and high maintenance cost for UI components and PDF document workflows.",
          action:
            "I refactored Angular architecture, standardized UI components, and stabilized document generation flows.",
          impact:
            "More maintainable codebase, faster technical onboarding, and reduced frontend debt.",
        },
      ];

  const mindset = isItalian
    ? [
        "Analisi del problema prima della tecnologia",
        "Architetture scalabili e realistiche",
        "Codice manutenibile e refactoring continuo",
        "Collaborazione cross-team strutturata",
        "Focus costante su performance e UX",
      ]
    : [
        "Problem analysis before tech choices",
        "Scalable and realistic architecture",
        "Maintainable code with continuous refactoring",
        "Structured cross-team collaboration",
        "Constant focus on performance and UX",
      ];

  const freelanceFocus = isItalian
    ? [
        "Sviluppo MVP",
        "Piattaforme web performanti",
        "Integrazione AI",
        "Automazione workflow",
        "Backend scalabile",
      ]
    : [
        "MVP development",
        "High-performance web platforms",
        "AI integration",
        "Workflow automation",
        "Scalable backend engineering",
      ];

  const experienceLogos: ExperienceLogo[] = [
    { name: "Accenture", src: "/brands/accenture.svg" },
    { name: "Saipem", src: "/brands/saipem.svg" },
    {
      name: "Vigili del Fuoco",
      src: "/brands/vigili-del-fuoco-round.png",
      sizeClass: "max-h-14",
    },
    { name: "Nexi", src: "/brands/nexi.svg" },
    { name: "pagoPA", src: "/brands/pagopa.svg" },
  ];

  const presentationSourceData: PresentationSourceData = {
    heroTitle: copy.heroTitle,
    heroSubtitle: copy.heroSubtitle,
    trustLine: copy.trustLine,
    heroStack,
    projects: projects.map((project) => ({
      title: project.title,
      role: project.role,
      client: project.client,
      stack: project.stack,
      problem: project.problem,
      action: project.action,
      impact: project.impact,
    })),
    techGroups: techGroups.map((group) => ({
      category: group.category,
      items: group.items,
    })),
    mindset,
    freelanceFocus,
  };

  useEffect(() => {
    if (!isAdmin) return;
    getDownloadMetrics().then((next) => {
      if (next) {
        setMetrics(next);
      }
    });
  }, [isAdmin]);

  return (
    <Layout>
      <div id="engineering-profile-page">
        <section className="relative flex min-h-[78vh] items-start overflow-hidden section-padding pt-6 md:min-h-0 md:pt-16 xl:min-h-[calc(100dvh-5.5rem)] xl:flex-col xl:justify-center xl:py-0">
          <div className="absolute inset-0 pattern-dots opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
          <div className="absolute top-[16%] right-[8%] w-80 h-80 rounded-full bg-primary/8 blur-3xl" />
          <div className="absolute bottom-[10%] left-[8%] w-72 h-72 rounded-full bg-accent/10 blur-3xl" />

          <div className="container-wide relative z-10">
            <div className="grid md:grid-cols-2 gap-10 md:gap-8 items-center">
              <div className="max-w-2xl text-left">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary font-mono text-xs tracking-wider uppercase mb-6">
                  <Sparkles className="w-3.5 h-3.5" />
                  {copy.heroEyebrow}
                </span>
                <motion.h1
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="page-hero-title mb-6"
                >
                  {copy.heroTitle.includes("<accent>") ? (
                    <>
                      {copy.heroTitle.split("<accent>")[0]}
                      <span className="text-shine">
                        {copy.heroTitle.split("<accent>")[1].replace("</accent>", "")}
                      </span>
                    </>
                  ) : (
                    copy.heroTitle
                  )}
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-body-lg text-muted-foreground max-w-2xl mb-8 leading-relaxed"
                >
                  {copy.heroSubtitle}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="flex flex-col sm:flex-row gap-4 no-print justify-start"
                >
                  <Button
                    variant="hero"
                    size="xl"
                    className="w-full sm:w-auto"
                    onClick={async () => {
                      await downloadStaticFile(`/${copy.cvAziendaleFileName}`, copy.cvAziendaleFileName);
                      trackEvent("pdf_cv_aziendale_download", {
                        page: "engineering_profile",
                      });
                    }}
                  >
                    <Download className="w-4 h-4" />
                    {copy.downloadCvAziendale}
                  </Button>
                  <Button variant="outline" size="xl" className="rotating-border w-full sm:w-auto" asChild>
                    <Link href={routes.contacts}>
                      {copy.contact}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </motion.div>
              </div>

              <div className="flex justify-center items-center">
                <EngineeringProfileHeroVisual isItalian={isItalian} />
              </div>
            </div>
          </div>
        </section>

        <InBreve
          text={
            isItalian
              ? "Cerchi uno sviluppatore freelance da inserire in un progetto o affiancare al tuo team? Sono Dario Marco Bellini: lavoro su tutto, dalla parte che vedono gli utenti a quella che gira sui server, con strumenti attuali come Next.js, React, TypeScript, Node, WordPress e Flutter. Parli con la persona che scrive il codice, non con un intermediario: stime oneste, tempi chiari e zero costi nascosti d'agenzia. Posso partire in fretta, integrarmi nei tuoi processi e lasciarti codice pulito che resta tuo. Scrivimi due righe sul progetto e ti dico subito come posso aiutarti e in quanto tempo."
              : "Looking for a freelance developer to join a project or work alongside your team? I'm Dario Marco Bellini: I handle everything, from the part users see to the part running on the servers, using current tools like Next.js, React, TypeScript, Node, WordPress and Flutter. You talk to the person who writes the code, not a middleman: honest estimates, clear timelines and no hidden agency costs. I can start quickly, fit into your existing processes and hand you clean code that stays yours. Send me a few lines about your project and I'll tell you right away how I can help and how long it will take."
          }
        />

        <section className="section-padding bg-surface/30 md:py-16">
          <div className="container-wide">
            <div className="mb-10 md:mb-12 text-left">
              <h2 className="font-display text-h2 font-bold mb-3">
                {copy.techTitle}
              </h2>
              <p className="text-body text-muted-foreground max-w-3xl">
                {copy.techSubtitle}
              </p>
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {techGroups.map((group, index) => (
                <motion.article
                  key={group.category}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="surface-card p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <group.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-display text-xl font-semibold">
                      {group.category}
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {group.items.map((item) => (
                      <span
                        key={item}
                        className="px-2.5 py-1 rounded-md border border-[hsl(var(--border))] bg-surface text-xs font-mono text-muted-foreground"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        <section id="experience-section" className="section-padding relative md:py-16">
          <div className="container-wide">
            <div className="mb-10 md:mb-12 text-left">
              <h2 className="font-display text-h2 font-bold mb-3">
                {copy.projectsTitle}
              </h2>
              <p className="text-body text-muted-foreground max-w-3xl">
                {copy.projectsSubtitle}
              </p>
            </div>

            <div className="relative">
              <div className="hidden md:block absolute left-3 top-0 bottom-0 w-px bg-gradient-to-b from-primary/70 via-primary/35 to-transparent" />
              <div className="space-y-6 md:space-y-8">
                {projects.map((project, index) => (
                  <motion.article
                    key={project.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.45, delay: index * 0.06 }}
                    className="md:pl-10"
                  >
                    <div className="surface-card p-6 md:p-7 lg:p-8">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <h3 className="font-display text-h3 font-semibold text-primary">
                          {project.title}
                        </h3>
                        {project.client && (
                          <span className="px-2.5 py-1 rounded-full border border-primary/35 bg-primary/10 text-[11px] font-mono text-primary">
                            {project.client}
                          </span>
                        )}
                      </div>
                      <p className="font-mono text-xs tracking-wide text-primary/85 mb-3">
                        {project.role}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.stack.map((tech) => (
                          <span
                            key={`${project.title}-${tech}`}
                            className="px-2 py-1 rounded-md border border-[hsl(var(--border))] bg-surface text-[11px] font-mono text-muted-foreground"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>

                      <div className="space-y-3.5 text-sm md:text-[0.98rem] leading-relaxed">
                        <div className="rounded-2xl border border-[hsl(var(--border))] bg-surface/70 p-4">
                          <p className="text-muted-foreground">
                            <span className="text-primary font-semibold">
                              {copy.problem}:{" "}
                            </span>
                            {project.problem}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-[hsl(var(--border))] bg-surface/70 p-4">
                          <p className="text-muted-foreground">
                            <span className="text-primary font-semibold">
                              {copy.action}:{" "}
                            </span>
                            {project.action}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                          <p className="text-foreground">
                            <span className="text-primary font-semibold">
                              {copy.impact}:{" "}
                            </span>
                            {project.impact}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-surface/30 md:py-16">
          <div className="container-wide">
            <div className="grid lg:grid-cols-2 gap-8">
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
                className="surface-card relative overflow-hidden border-primary/20 bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.1),transparent_48%)] p-6 shadow-[0_16px_42px_rgba(0,0,0,0.14)] md:p-8"
              >
                <h2 className="mb-4 font-display text-h3 font-bold text-primary text-left">
                  {copy.mindsetTitle}
                </h2>
                <p className="mb-5 text-body leading-relaxed text-muted-foreground text-left">
                  {copy.mindsetSubtitle}
                </p>
                <ul className="space-y-3">
                  {mindset.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 rounded-xl border border-primary/20 bg-background/80 px-3 py-2.5 text-sm text-foreground"
                    >
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.article>

              <motion.article
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.08 }}
                className="surface-card relative overflow-hidden border-primary/20 bg-[radial-gradient(circle_at_bottom_left,hsl(var(--primary)/0.11),transparent_52%)] p-6 shadow-[0_16px_42px_rgba(0,0,0,0.14)] md:p-8"
              >
                <h2 className="mb-4 font-display text-h3 font-bold text-primary text-left">
                  {copy.freelanceTitle}
                </h2>
                <p className="mb-5 text-body leading-relaxed text-muted-foreground text-left">
                  {copy.freelanceSubtitle}
                </p>
                <div className="mb-6 flex gap-2 overflow-x-auto whitespace-nowrap md:flex-wrap md:overflow-visible">
                  {freelanceFocus.map((item) => (
                    <span
                      key={item}
                      className="shrink-0 rounded-md border border-primary/25 bg-background/80 px-2.5 py-1 text-xs font-mono text-foreground"
                    >
                      {item}
                    </span>
                  ))}
                </div>
                <Button variant="outline" className="rotating-border w-full sm:w-auto" asChild>
                  <Link href={routes.contacts}>
                    {copy.discuss}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </motion.article>
            </div>
          </div>
        </section>

        <section className="section-padding pt-0">
          <div className="container-wide">
            <div className="surface-card border-primary/20 bg-[radial-gradient(circle_at_top,hsl(var(--primary)/0.08),transparent_58%)] p-6 md:p-8">
              <h2 className="mb-3 text-left font-display text-h3 font-bold text-foreground">
                {copy.logosTitle}
              </h2>
              <p className="mb-5 text-body text-muted-foreground text-left">
                {copy.logosSubtitle}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {experienceLogos.map((logo) => (
                  <div
                    key={logo.name}
                    className="flex items-center justify-center rounded-xl border border-[hsl(var(--border))] bg-surface/80 px-4 py-6"
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={logo.src}
                      alt={`${logo.name} logo`}
                      loading="lazy"
                      className={`h-auto w-auto object-contain max-h-8 ${
                        logo.sizeClass ?? ""
                      }`}
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-muted-foreground/80 font-mono">
                {copy.logosNote}
              </p>
            </div>
          </div>
        </section>

        <section className="section-padding pt-0 no-print">
          <div className="container-wide">
            <div className="surface-card p-8 md:p-10 text-left">
              <h2 className="font-display text-h3 font-bold mb-3">
                {copy.pdfTitle}
              </h2>
              <p className="text-body text-muted-foreground mb-6 max-w-2xl">
                {copy.pdfSubtitle}
              </p>
              <div className="flex flex-col items-start gap-4">
                <div className="flex flex-wrap justify-start gap-3">
                {/* TODO: riabilitare CV tecnico e Vision & Mission quando i PDF statici saranno pronti */}
                <Button
                  variant="hero"
                  size="lg"
                  className="h-auto min-h-12 px-6 py-3 justify-center whitespace-nowrap text-center touch-manipulation"
                  onClick={async () => {
                    await downloadStaticFile(`/${copy.cvAziendaleFileName}`, copy.cvAziendaleFileName);
                    trackEvent("pdf_cv_aziendale_download", {
                      page: "engineering_profile",
                    });
                  }}
                >
                  <FileText className="w-4 h-4" />
                  <span className="leading-snug">
                    {copy.downloadCvAziendale}
                  </span>
                </Button>
                {showPresentationDownload ? (
                  <Button
                    variant="heroOutline"
                    size="lg"
                    className="w-full h-auto min-h-12 px-4 py-3 justify-start sm:justify-center whitespace-normal text-left sm:text-center touch-manipulation"
                    onClick={async () => {
                      await downloadEngineeringSlidesPdf({
                        fileName: copy.pdfSlidesFileName,
                        isItalian,
                        heroTitle: copy.heroTitle,
                        heroSubtitle: copy.heroSubtitle,
                        trustLine: copy.trustLine,
                        heroStack,
                        techGroups: techGroups.map((group) => ({
                          category: group.category,
                          items: group.items,
                        })),
                        mindsetItems: mindset,
                        freelanceItems: freelanceFocus,
                        projects,
                        problemLabel: copy.problem,
                        actionLabel: copy.action,
                        impactLabel: copy.impact,
                      });
                      trackEvent("pdf_presentation_download", {
                        page: "engineering_profile",
                      });
                      const next = await recordDownloadMetric(
                        "presentation_pdf",
                        isItalian ? "it" : "en"
                      );
                      if (next) setMetrics(next);
                    }}
                  >
                    <Layers className="w-4 h-4" />
                    <span className="leading-snug">{copy.downloadSlides}</span>
                  </Button>
                ) : null}
                </div>
              </div>
              {isAdmin && metrics ? (
                <div className="mt-6 rounded-lg border border-primary/25 bg-primary/5 p-4 text-left md:text-center">
                  <p className="text-xs font-mono uppercase tracking-wider text-primary mb-2">
                    {copy.privateMonitoring}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div className="rounded-md border border-white/10 bg-background/40 px-3 py-2">
                      <p className="text-muted-foreground">Full PDF</p>
                      <p className="text-foreground font-semibold">
                        {metrics.full_pdf}
                      </p>
                    </div>
                    <div className="rounded-md border border-white/10 bg-background/40 px-3 py-2">
                      <p className="text-muted-foreground">Experience PDF</p>
                      <p className="text-foreground font-semibold">
                        {metrics.experience_pdf}
                      </p>
                    </div>
                    <div className="rounded-md border border-white/10 bg-background/40 px-3 py-2">
                      <p className="text-muted-foreground">Presentation PDF</p>
                      <p className="text-foreground font-semibold">
                        {metrics.presentation_pdf}
                      </p>
                    </div>
                    <div className="rounded-md border border-white/10 bg-background/40 px-3 py-2">
                      <p className="text-muted-foreground">Open Fullscreen</p>
                      <p className="text-foreground font-semibold">
                        {metrics.presentation_open}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export function EngineeringProfileClient() {
  return (
    <AuthProvider>
      <EngineeringProfileInner />
    </AuthProvider>
  );
}
